import React from 'react';
import { Check, AlertTriangle, FileText } from 'lucide-react';

const formatEur = (val) => `${(val || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`;

/**
 * Étape 6 TICGN — Validation pré-génération.
 * Affichage du verdict d'éligibilité multi-voies + bouton de génération.
 * Extrait de CreateRapportTICGN.jsx pour réduire la taille du parent.
 */
export const StepValidationTICGN = ({ ctx }) => {
  const {
    formData, consommationsParPCE, allPCEs, nafEligMap, rapportType,
    loading, searchParams, handleGenerate,
    vpActivated, onActivateVP,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-6" data-testid="step-validation">
      <h2 className="text-lg font-semibold text-[#191D1E] mb-4">Validation avant generation</h2>

      {/* Bloc verification eligibilite */}
      {(() => {
        const vac = parseFloat(formData.valeur_ajoutee_siren) || 0;
        const tarifCerfa = 8.37;
        let totalConsoKwh = 0;
        allPCEs.forEach(pce => {
          const consos = consommationsParPCE[pce.pce] || {};
          Object.values(consos).forEach(v => { totalConsoKwh += parseFloat(v) || 0; });
        });
        const totalConsoMwh = totalConsoKwh / 1000;
        const ticgnCerfa = totalConsoMwh * tarifCerfa;
        const ratio = vac > 0 ? (ticgnCerfa / vac) : 0;
        const ratioPercent = (ratio * 100).toFixed(3);
        const ratioOk = ratio >= 0.005;

        const pceStatuses = allPCEs.map(pce => {
          const code = (pce.code_naf || '').trim().toUpperCase();
          const etype = nafEligMap[code];
          const nafLoaded = code in nafEligMap;
          const nafQualifie = etype === 'EXONERATION' || etype === 'TARIF_REDUIT'
            || etype === 'TARIF_REDUIT_AVEC_RATIO' || etype === 'TARIF_REDUIT_SANS_RATIO';
          const isExo = etype === 'EXONERATION';
          const eligible = nafQualifie && (isExo || ratioOk);
          return { pce: pce.pce, code, etype, nafLoaded, nafQualifie, isExo, eligible };
        });
        const anyLoading = pceStatuses.some(s => !s.nafLoaded);
        const anyNafQualifie = pceStatuses.some(s => s.nafQualifie);
        const nafBlocant = !anyNafQualifie && !anyLoading && pceStatuses.length > 0;

        const voieA_eligible = pceStatuses.some(s => s.nafQualifie && !s.isExo) && ratioOk;
        const voieAbis_eligible = pceStatuses.some(s => s.isExo);

        const ca = parseFloat(formData.valeur_production?.ca) || 0;
        const exp = parseFloat(formData.valeur_production?.export) || 0;
        const imp = parseFloat(formData.valeur_production?.import) || 0;
        const vp = ca + exp - imp;
        const totalFact = (formData.lignes_energies || []).reduce(
          (s, l) => s + (parseFloat(l.consommation_mwh) || 0) * (parseFloat(l.tarif_fiscal) || 0), 0
        );
        const ratioVP = vp > 0 ? (totalFact / vp) : 0;
        const voieB_renseignee = vp > 0 && totalFact > 0;
        const voieB_eligible = voieB_renseignee && ratioVP > 0.03;

        const voieBbis_sirets = [];
        if (rapportType === 'multi') {
          (formData.sites || []).forEach((site, sIdx) => {
            const key = site.siret || `site-${sIdx}`;
            const v = formData.vp_par_siret?.[key] || {};
            const vCA = parseFloat(v.ca) || 0;
            const vExp = parseFloat(v.export) || 0;
            const vImp = parseFloat(v.import) || 0;
            const vVP = vCA + vExp - vImp;
            const vF = (v.lignes_energies || []).reduce(
              (s, l) => s + (parseFloat(l.consommation_mwh) || 0) * (parseFloat(l.tarif_fiscal) || 0), 0
            );
            if (vVP > 0 && vF > 0) {
              const r = vF / vVP;
              voieBbis_sirets.push({
                siret: site.siret, nom: site.nom_site || '',
                ratio: r, eligible: r > 0.03,
              });
            }
          });
        }
        const voieBbis_renseignee = voieBbis_sirets.length > 0;
        const voieBbis_eligible = voieBbis_sirets.some(s => s.eligible);

        const quotasCochees = !!formData.soumis_quotas_co2 ||
          (rapportType === 'multi' && (formData.sites || []).some(s => s.soumis_quotas_co2));
        const voieC_eligible = quotasCochees && ratioOk;

        const pcesAvecDU = allPCEs.filter(p => {
          const du = p.double_usage || {};
          const valeur = parseFloat(du.valeur) || 0;
          return du.active && valeur > 0 && (du.cas_code || du.raison);
        });
        const voieD_eligible = pcesAvecDU.length > 0;

        const pcesAvecICPE = allPCEs.filter(p => p.icpe_rubrique);
        const voieE_eligible = pcesAvecICPE.length > 0;

        const isEligible = voieA_eligible || voieAbis_eligible || voieB_eligible
          || voieBbis_eligible || voieC_eligible || voieD_eligible || voieE_eligible;
        const showLoading = anyLoading && (ratioOk || voieB_eligible);

        const voies = [
          {
            key: 'A',
            titre: 'Ratio CERFA TICGN/VAC ≥ 0,5 %',
            sous_titre: `Ratio calculé : ${ratioPercent.replace('.', ',')} %`,
            detail: `TICGN CERFA ${formatEur(ticgnCerfa)} / VAC ${formatEur(vac)} — Nécessite un code NAF tarif réduit qualifiant.`,
            statut: voieA_eligible ? 'eligible' : (anyNafQualifie && !ratioOk ? 'rate_ratio' : (!anyNafQualifie ? 'rate_naf' : 'rate')),
          },
          {
            key: 'A_exo',
            label: 'A bis',
            titre: 'Exonération NAF (sans condition de ratio)',
            sous_titre: voieAbis_eligible
              ? `${pceStatuses.filter(s => s.isExo).length} PCE(s) avec NAF d'exonération`
              : 'Aucun PCE avec un code NAF d\'exonération',
            detail: 'Codes NAF classés "EXONERATION" — éligibilité d\'office.',
            statut: voieAbis_eligible ? 'eligible' : 'na',
          },
          {
            key: 'B',
            titre: 'Ratio Valeur de Production > 3 %',
            sous_titre: voieB_renseignee
              ? `Ratio calculé : ${(ratioVP * 100).toFixed(2).replace('.', ',')} % (Σ Conso × Tarif ${formatEur(totalFact)} / VP ${formatEur(vp)})`
              : 'Non renseigné (étape 2, bloc violet)',
            detail: 'Tarif réduit RATIO_VALEUR_PRODUCTION — entreprise dite "énergo-intensive" (art. 265 nonies CdD). Ratio = Σ (Conso × Tarif fiscal) / (CA + Export − Import).',
            statut: voieB_eligible ? 'eligible' : (voieB_renseignee ? 'rate' : 'na'),
          },
          ...(rapportType === 'multi' ? [{
            key: 'Bbis',
            label: 'B bis',
            titre: 'VP par SIRET (fallback multi-sites)',
            sous_titre: voieBbis_renseignee
              ? `${voieBbis_sirets.filter(s => s.eligible).length}/${voieBbis_sirets.length} SIRET(s) > 3 %`
              : 'Non renseigné (étape 2, variante VP par SIRET)',
            detail: voieBbis_renseignee
              ? voieBbis_sirets.map(s => `${s.siret || '—'}${s.nom ? ' (' + s.nom + ')' : ''} : ${(s.ratio * 100).toFixed(2).replace('.', ',')} % ${s.eligible ? '✓' : '✗'}`).join(' • ')
              : 'Permet de qualifier individuellement les SIRETs si le ratio SIREN ne passe pas.',
            statut: voieBbis_eligible ? 'eligible' : (voieBbis_renseignee ? 'rate' : 'na'),
          }] : []),
          {
            key: 'C',
            titre: 'Soumis aux Quotas CO2 (SEQE-UE)',
            sous_titre: quotasCochees
              ? (ratioOk ? 'Coché — ratio TICGN/VAC ≥ 0,5 % validé' : 'Coché mais ratio TICGN/VAC < 0,5 %')
              : 'Non coché (étape 2)',
            detail: 'Ouvre le motif tarif réduit QUOTAS_CO2 (toujours soumis au ratio TICGN/VAC ≥ 0,5 %).',
            statut: voieC_eligible ? 'eligible' : (quotasCochees && !ratioOk ? 'rate_ratio' : 'na'),
          },
          {
            key: 'D',
            titre: 'Double usage (exonération matières premières)',
            sous_titre: voieD_eligible
              ? `${pcesAvecDU.length} PCE(s) avec cas réglementaire`
              : 'Aucun PCE coché en double usage',
            detail: voieD_eligible
              ? pcesAvecDU.map(p => `${p.pce}${p.double_usage?.cas_libelle ? ' — ' + p.double_usage.cas_libelle : ''}`).join(' • ')
              : 'Cocher dans la fiche PCE (étape 3) avec un cas réglementaire (art. 265 nonies CdD).',
            statut: voieD_eligible ? 'eligible' : 'na',
          },
          {
            key: 'E',
            titre: 'Rubrique ICPE qualifiante',
            sous_titre: voieE_eligible
              ? `${pcesAvecICPE.length} PCE(s) avec rubrique ICPE`
              : 'Aucune rubrique ICPE renseignée',
            detail: voieE_eligible
              ? pcesAvecICPE.map(p => `${p.pce} — Rubrique ${p.icpe_rubrique}`).join(' • ')
              : 'Sélectionner dans la fiche PCE (étape 3) — produits minéraux non métalliques notamment.',
            statut: voieE_eligible ? 'eligible' : 'na',
          },
        ];

        const statutBadge = (s) => {
          if (s === 'eligible') return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-semibold">✓ Éligible</span>;
          if (s === 'rate') return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">✗ Seuil non atteint</span>;
          if (s === 'rate_ratio') return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">✗ Ratio &lt; 0,5 %</span>;
          if (s === 'rate_naf') return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">✗ NAF non qualifiant</span>;
          return <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">— Non applicable</span>;
        };

        return (
          <div className={`border-2 rounded-lg p-5 mb-6 ${isEligible ? 'border-emerald-300 bg-emerald-50/50' : 'border-red-300 bg-red-50/50'}`} data-testid="validation-calculs">
            <h3 className="text-base font-semibold text-[#191D1E] mb-4">Vision éligibilité TICGN — toutes les voies</h3>

            <div className="space-y-2 mb-5" data-testid="validation-voies">
              {voies.map(v => (
                <div
                  key={v.key}
                  data-testid={`voie-${v.key}`}
                  className={`p-3 rounded-lg border ${
                    v.statut === 'eligible' ? 'border-emerald-300 bg-emerald-50' :
                    v.statut === 'na' ? 'border-gray-200 bg-white' :
                    'border-amber-300 bg-amber-50/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-[#191D1E]">
                        <span className="inline-block w-12 text-[#687175] font-mono">{v.label || (v.key + '.')}</span>
                        {v.titre}
                      </div>
                      <div className="text-xs text-[#687175] mt-0.5 ml-12">{v.sous_titre}</div>
                    </div>
                    <div className="shrink-0" data-testid={`voie-${v.key}-statut`}>{statutBadge(v.statut)}</div>
                  </div>
                  <div className="ml-12 text-[11px] text-[#687175] italic">{v.detail}</div>
                </div>
              ))}
            </div>

            <details className="mt-3 pt-3 border-t border-gray-200">
              <summary className="text-xs font-semibold text-[#687175] uppercase cursor-pointer select-none">
                Détail éligibilité NAF par PCE ({pceStatuses.length})
              </summary>
              <div className="space-y-1.5 mt-2">
                {pceStatuses.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-1" data-testid={`pce-naf-status-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-semibold text-[#191D1E]">{s.pce}</span>
                      <span className="text-xs text-[#687175]">{s.code || <span className="italic">Code NAF manquant</span>}</span>
                    </div>
                    <div>
                      {!s.nafLoaded ? (
                        <span className="text-xs text-[#687175] italic">Chargement…</span>
                      ) : s.nafQualifie ? (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                          {s.etype === 'EXONERATION' ? 'Exonération' : 'Tarif réduit possible'}
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                          Non qualifiant ({s.etype || 'aucune règle'})
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </details>

            <div className={`mt-4 flex items-start gap-3 p-3 rounded-lg ${isEligible ? 'bg-emerald-100' : 'bg-red-100'}`} data-testid="validation-verdict">
              {isEligible ? (
                <><Check className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-emerald-800">ELIGIBLE</span>
                    <span className="text-sm text-emerald-700 ml-2">
                      {showLoading ? 'Vérification NAF en cours…' :
                        `Voie(s) ouverte(s) : ${voies.filter(v => v.statut === 'eligible').map(v => v.key).join(', ')}`}
                    </span>
                  </div></>
              ) : (
                <><AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <span className="font-semibold text-red-800">NON ELIGIBLE</span>
                    <span className="text-sm text-red-700 ml-2 block">
                      {showLoading ? 'Vérification NAF en cours…' :
                        nafBlocant ? 'Aucun PCE n\'a de code NAF qualifiant et aucune voie alternative (VP, ICPE, double usage) n\'est renseignée.' :
                        !ratioOk && !voieB_eligible && !voieBbis_eligible && !voieD_eligible && !voieE_eligible ? `Aucune voie ne passe : ratio CERFA ${ratioPercent.replace('.', ',')}% < 0,500% et aucun fallback (VP, ICPE, double usage) renseigné.` :
                        'Conditions d\'éligibilité non réunies.'}
                    </span>
                  </div></>
              )}
            </div>

            {/* CTA Activation VP — gros si non éligible, petit lien si éligible */}
            {!vpActivated && !isEligible && (
              <div className="mt-4 p-4 rounded-lg border-2 border-purple-400 bg-purple-50" data-testid="vp-activate-cta">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">💜</div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-purple-900 mb-1">
                      Activer le tarif réduit par Valeur de Production ?
                    </h4>
                    <p className="text-xs text-purple-800 mb-3">
                      Aucune voie d'éligibilité classique ne passe. Vous pouvez tenter le motif
                      <strong> RATIO_VALEUR_PRODUCTION</strong> (art. 265 nonies CdD) : si
                      <strong> Σ (Conso × Tarif fiscal) / (CA + Export − Import) &gt; 3 %</strong>,
                      l'entreprise est qualifiée d'énergo-intensive et bénéficie du tarif réduit.
                    </p>
                    <button
                      type="button"
                      onClick={onActivateVP}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
                      data-testid="activate-vp-btn"
                    >
                      Activer la Valeur de Production →
                    </button>
                  </div>
                </div>
              </div>
            )}
            {!vpActivated && isEligible && (
              <div className="mt-3 text-right">
                <button
                  type="button"
                  onClick={onActivateVP}
                  className="text-xs text-purple-700 hover:text-purple-900 hover:underline inline-flex items-center gap-1"
                  data-testid="activate-vp-link"
                >
                  💜 Vérifier aussi la Valeur de Production (optionnel) →
                </button>
              </div>
            )}
            {vpActivated && (
              <div className="mt-3 p-2 rounded bg-purple-100 border border-purple-300 text-xs text-purple-900 text-center" data-testid="vp-activated-banner">
                💜 Valeur de Production activée — voir l'étape suivante pour la renseigner et générer le rapport.
              </div>
            )}
          </div>
        );
      })()}

      {!vpActivated && (
        <button onClick={handleGenerate} disabled={loading}
          className="w-full bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white py-4 rounded-xl font-semibold text-lg hover:from-[#EA580C] hover:to-[#C2410C] disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="generate-btn">
          {loading ? <><span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> Calcul en cours...</> : <><FileText className="w-5 h-5" /> {searchParams.get('edit') ? 'Mettre a jour le rapport' : 'Generer le Rapport Digital'}</>}
        </button>
      )}
    </div>
  );
};

export default StepValidationTICGN;
